import React, { useEffect, useState } from "react";
import { useIncomeStatementStore } from "../../../config/stores";
import { Card, Space, Alert, Spin, Form, Typography, message } from "antd";
import IncomeStatementTable from "./IncomeStatementTable";
import { find, isEmpty } from "lodash";
import AppButton from "../../../components/common/AppButton";
import AppInput from "../../../components/common/AppInput";
import { AiOutlineFileExcel, AiOutlineReload, AiOutlineFilePdf } from "react-icons/ai";
import dayjs from "dayjs";
import generateIncomeStatementPdf from "../../../config/services/incomeStatementPdf";

const { Title } = Typography;

const IncomeStatement = () => {
  const incomeStatementStore = useIncomeStatementStore();
  const [incomeStatement, setIncomeStatement] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [form] = Form.useForm();
  const [startDate, setStartDate] = useState(dayjs().startOf("month"));
  const [endDate, setEndDate] = useState(dayjs());

  const getDefaultContext = () => {
    const startDateValue = startDate ? dayjs(startDate).format("YYYY-MM-DD") : dayjs().startOf("month").format("YYYY-MM-DD");
    const endDateValue = endDate ? dayjs(endDate).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD");
    return { startDate: startDateValue, endDate: endDateValue };
  };

  useEffect(() => {
    const defaultContext = getDefaultContext();
    form.setFieldsValue({
      startDate: dayjs(defaultContext.startDate),
      endDate: dayjs(defaultContext.endDate),
    });
  }, []);

  useEffect(() => {
    const defaultContext = getDefaultContext();
    const existingData = find(incomeStatementStore.incomeStatement, (records) => {
      return (
        records?.startDate === defaultContext.startDate &&
        records?.endDate === defaultContext.endDate
      );
    });

    if (existingData) {
      const data = existingData?.data;
      const incomeStatementData = data?.data || data;
      setIncomeStatement(incomeStatementData);
      setHasLoaded(true);
    }
  }, [incomeStatementStore.incomeStatement, startDate, endDate]);

  const handleGetIncomeStatement = () => {
    const defaultContext = getDefaultContext();
    if (dayjs(defaultContext.endDate).isBefore(dayjs(defaultContext.startDate))) {
      message.error("End Date cannot be before Start Date");
      return;
    }
    setHasLoaded(false);
    incomeStatementStore.getIncomeStatement(defaultContext).finally(() => {
      setHasLoaded(true);
    });
  };

  const reloadIncomeStatement = () => {
    handleGetIncomeStatement();
  };

  const downloadIncomeStatement = () => {
    const defaultContext = getDefaultContext();
    const fileName = `INCOME_STATEMENT_${defaultContext.startDate}_TO_${defaultContext.endDate}.xlsx`;
    incomeStatementStore.downloadIncomeStatement(defaultContext, fileName);
  };

  const downloadIncomeStatementPdf = () => {
    if (incomeStatement) {
      const defaultContext = getDefaultContext();
      generateIncomeStatementPdf(incomeStatement, defaultContext.startDate, defaultContext.endDate);
    }
  };

  const isLoading =
    incomeStatementStore.gettingIncomeStatement && !hasLoaded;
  const hasError = !isEmpty(incomeStatementStore.incomeStatementError);
  const hasData =
    incomeStatement?.income?.items?.length > 0 ||
    incomeStatement?.expenditure?.items?.length > 0;
  const currentStartDate = startDate ? dayjs(startDate).format("YYYY-MM-DD") : dayjs().startOf("month").format("YYYY-MM-DD");
  const currentEndDate = endDate ? dayjs(endDate).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD");

  return (
    <div>
      <Card
        bordered={false}
        title={
          <div>
            <Title level={4} style={{ margin: 0 }}>
              INCOME STATEMENT
            </Title>
            <div style={{ fontSize: "14px", color: "#666", marginTop: "4px" }}>
              Period: {currentStartDate} to {currentEndDate}
            </div>
          </div>
        }
        extra={
          <Space>
            <AppButton
              text="RELOAD"
              loadingText="RELOADING..."
              loading={incomeStatementStore.gettingIncomeStatement}
              iconBefore={<AiOutlineReload />}
              onClick={reloadIncomeStatement}
            />
            <AppButton
              text="DOWNLOAD EXCEL"
              danger
              type="dashed"
              loadingText="DOWNLOADING..."
              iconBefore={<AiOutlineFileExcel />}
              loading={incomeStatementStore.downloadingIncomeStatement}
              onClick={downloadIncomeStatement}
              disabled={!hasData}
            />
            <AppButton
              text="DOWNLOAD PDF"
              type="primary"
              loadingText="GENERATING..."
              iconBefore={<AiOutlineFilePdf />}
              onClick={downloadIncomeStatementPdf}
              disabled={!hasData}
            />
          </Space>
        }
      >
        <Form
          form={form}
          layout="inline"
          onFinish={handleGetIncomeStatement}
          style={{ marginBottom: "16px" }}
        >
          <AppInput
            name="startDate"
            label="Start Date"
            type="date"
            rules={[{ required: true, message: "Please select a start date" }]}
            inputAttributes={{
              onChange: (date) => {
                setStartDate(date);
              },
            }}
          />
          <AppInput
            name="endDate"
            label="End Date"
            type="date"
            rules={[{ required: true, message: "Please select an end date" }]}
            inputAttributes={{
              onChange: (date) => {
                setEndDate(date);
              },
              disabledDate: (current) => {
                return current && startDate && current.isBefore(dayjs(startDate), "day");
              },
            }}
          />
          <Form.Item>
            <AppButton
              text="GET INCOME STATEMENT"
              loadingText="Loading..."
              loading={incomeStatementStore.gettingIncomeStatement}
              htmlType="submit"
            />
          </Form.Item>
        </Form>
        {isLoading ? (
          <div style={{ textAlign: "center", padding: "50px" }}>
            <Spin size="large" />
            <div style={{ marginTop: "16px" }}>Loading income statement...</div>
          </div>
        ) : hasError ? (
          <Alert
            type="error"
            showIcon
            message="Error Loading Income Statement"
            description={
              <div>
                <div style={{ marginBottom: "8px" }}>
                  <strong>Error:</strong>{" "}
                  {incomeStatementStore.incomeStatementError?.response?.data
                    ?.server?.message ||
                    incomeStatementStore.incomeStatementError?.response?.data
                      ?.message ||
                    incomeStatementStore.incomeStatementError?.message ||
                    "Unable to load income statement."}
                </div>
                {incomeStatementStore.incomeStatementError?.response?.status ===
                  404 && (
                  <div
                    style={{
                      marginTop: "12px",
                      padding: "12px",
                      backgroundColor: "#fff3cd",
                      borderRadius: "4px",
                      fontSize: "12px",
                    }}
                  >
                    <div style={{ marginBottom: "8px" }}>
                      <strong>404 - Resource Not Found</strong>
                    </div>
                    <div style={{ marginBottom: "4px" }}>
                      The endpoint{" "}
                      <code>/api/v1/finance/income-statement</code> was not
                      found.
                    </div>
                    <div style={{ marginTop: "8px" }}>
                      <strong>Backend Setup Required:</strong>
                      <ul
                        style={{
                          marginTop: "4px",
                          marginLeft: "20px",
                          padding: 0,
                        }}
                      >
                        <li>
                          Ensure the <code>incomeStatementRouter</code> is
                          mounted in your main finance router:
                        </li>
                        <li
                          style={{
                            fontFamily: "monospace",
                            marginTop: "4px",
                          }}
                        >
                          <code>
                            router.use('/income-statement', incomeStatementRouter);
                          </code>
                        </li>
                        <li style={{ marginTop: "8px" }}>
                          Check that the route file is imported and registered
                        </li>
                        <li>
                          Verify the server is running and the route is
                          accessible
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            }
          />
        ) : !hasData ? (
          <Alert
            type="info"
            showIcon
            message="No Income Statement Data"
            description="No income statement data available for the selected period."
          />
        ) : (
          <IncomeStatementTable incomeStatementData={incomeStatement} />
        )}
      </Card>
    </div>
  );
};

export default IncomeStatement;



